"use client";

import { useState } from "react";
import { TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import { BONDS, type Bond } from "@/lib/tool-kit/chem-reference/bond-data";

export interface BondTableStrings {
  tableHeading: string;
  filterLabel: string;
  filterPlaceholder: string;
  columnBond: string;
  columnEnergy: string;
  columnAdd: string;
  addBroken: string;
  addFormed: string;
  addBrokenLabel: string;
  addFormedLabel: string;
  noBonds: string;
  tableSource: string;
}

function matches(bond: Bond, query: string): boolean {
  const q = query.trim();
  if (q === "") {
    return true;
  }
  return (
    bond.label.toLowerCase().includes(q.toLowerCase()) ||
    bond.a.toLowerCase() === q.toLowerCase() ||
    bond.b.toLowerCase() === q.toLowerCase()
  );
}

/** The whole table, filterable, with buttons that add a bond to either side. */
export function BondTable({
  strings,
  fmt,
  onAdd,
}: {
  strings: BondTableStrings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  onAdd: (bond: Bond, side: "broken" | "formed") => void;
}) {
  const [query, setQuery] = useState("");
  const rows = BONDS.filter((bond) => matches(bond, query));
  return (
    <div className="space-y-3">
      <TextField
        id="be-filter"
        label={strings.filterLabel}
        onChange={setQuery}
        placeholder={strings.filterPlaceholder}
        type="search"
        value={query}
      />
      <div className="max-h-96 overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {strings.tableHeading}
          </caption>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.columnBond}
              </th>
              <th className={TH} scope="col">
                {strings.columnEnergy}
              </th>
              <th className={TH} scope="col">
                {strings.columnAdd}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((bond) => (
              <tr key={bond.label}>
                <td className={`${TD} font-mono`}>{bond.label}</td>
                <td className={TD}>
                  {fmt(bond.energy, { useGrouping: false })}
                </td>
                <td className={TD}>
                  <span className="flex gap-2">
                    <button
                      aria-label={t(strings.addBrokenLabel, {
                        bond: bond.label,
                      })}
                      className={BUTTON_SMALL}
                      onClick={() => onAdd(bond, "broken")}
                      type="button"
                    >
                      {strings.addBroken}
                    </button>
                    <button
                      aria-label={t(strings.addFormedLabel, {
                        bond: bond.label,
                      })}
                      className={BUTTON_SMALL}
                      onClick={() => onAdd(bond, "formed")}
                      type="button"
                    >
                      {strings.addFormed}
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="p-3 text-muted-foreground text-sm">{strings.noBonds}</p>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">{strings.tableSource}</p>
    </div>
  );
}
